import type {State as ReduxState, State} from 'src/reducers';
import type {AudienceEntity} from 'src/types/multientity-audience';
import type {EntitySummary, PlacementSummary} from 'src/types/entity-summaries';
import type {PersonRelatedSummary} from 'src/types/entity-summaries.snake';
import type {EntityType, EntityAttribute} from 'src/types/ats-entities';
// @ts-expect-error - TS2614 - Module '"src/api-parsers/index"' has no exported member 'AudienceMemberTags'. Did you mean to use 'import AudienceMemberTags from "src/api-parsers/index"' instead?
import type {AudienceMemberTags} from 'src/api-parsers/index';

import {createSelector} from 'reselect';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';

import {values} from 'src/utils/object';
import {emptyObject, emptyArray} from 'src/utils';
import {selectEntityDirectAttributesByType} from 'src/selectors/ats-entities';


export type LegacyAudienceObject = {
  memberList: LegacyAudienceMember[];
  numPages: number;
  numTotalMembers: number;
  page: number;
  workflowId?: string;
};

export type LegacyAudienceMember = {
  id: string;
  firstName?: string;
  lastName?: string;
  fullname?: string | null;
};

export type ModifiedLegacyAudienceMember = LegacyAudienceMember &
  AudienceEntity & {
    name?: null | string;
    firstname?: null | string;
    lastname?: null | string;
    candidate?: any;
    tags?: AudienceMemberTags;
  };

const entities = (state: ReduxState) => state.multientityAudience.entities;
export const selectRelatedEntities = (state: State) =>
  state.multientityAudience.related;

const getIncomingEntities = (state: ReduxState): any =>
  state.multientityAudience.incoming;
const getAtsMappingsByUrl = (state: any) =>
  keyBy(state.atsEntities.entities, 'url');

export const selectEntity: (state: ReduxState, id: string) => AudienceEntity =
  createSelector(
    entities,
    // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'id' implicitly has an 'any' type.
    (state, id) => id,
    (entities, id) => entities[id],
  );

export const selectEntitySummary = (
  state: ReduxState,
  entityType: EntityType,
  audienceMemberId: string,
): any =>
  state?.multientityAudience?.related?.[entityType]?.[audienceMemberId] || {};

export const entityAsLegacyEntity = (
  entity: AudienceEntity,
): ModifiedLegacyAudienceMember => {
  return {
    ...entity,
    ...(entity.data ? entity.data : {}),
    tags: entity.data,
    id: entity.id,
  };
};

export const expandedEntityAsLegacyEntity = (
  entity: AudienceEntity,
): ModifiedLegacyAudienceMember => {
  return {
    ...entity,
    ...(entity?.data || {}),
    tags: entity.data,
    id: entity.id,
    // TODO (rng) - Massive ugly hack, this works because
    // Appointment/Placement/JobSubmission have either a 'candidate' or
    // 'candidateReference' field as the candidate object
    candidate:
      entity.outgoingRelatedEntityIds?.candidate ||
      entity.outgoingRelatedEntityIds?.candidateReference ||
      entity.outgoingRelatedEntityIds?.pcrCandidate ||
      {},
  };
};

export const selectMultientityAudienceAsLegacyAudience: (
  state: ReduxState,
  page: number,
  entity: string,
) => LegacyAudienceObject = createSelector(
  // TODO(marcos): use getAtsCandidateName here instead?
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'page' implicitly has an 'any' type. | TS7006 - Parameter 'entity' implicitly has an 'any' type.
  (state, page, entity) => state.multientityAudience?.[entity]?.[page],
  entities,
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'page' implicitly has an 'any' type.
  (state, page) => page,
  (audiencePage, entities, page) =>
    audiencePage
      ? {
          // TODO(marcos): replace this with a better implementation
          // @ts-expect-error - TS7006 - Parameter 'entityId' implicitly has an 'any' type.
          memberList: audiencePage.entitiesById.map((entityId) =>
            entityAsLegacyEntity(entities[entityId]),
          ),
          numPages: audiencePage.numPages,
          numTotalMembers: audiencePage.totalEntities,
          page,
          isLoaded: true,
        }
      : {
          memberList: [],
          numPages: 0,
          numTotalMembers: 0,
          page,
          isLoaded: false,
        },
);

export const selectMultientityWorkflowAudienceCount = (
  state: ReduxState,
  workflowId: string,
): any =>
  state.multientityAudience?.workflows?.[workflowId]?.['1']?.totalEntities;

// @ts-expect-error - TS2322 - Type 'OutputParametricSelector<any, any, { memberList: any; numPages: any; numTotalMembers: any; page: number; workflowId: any; } | null, (res1: any, res2: any, res3: any, res4: any) => { memberList: any; numPages: any; numTotalMembers: any; page: number; workflowId: any; } | null>' is not assignable to type '(state: any, page: string, workflowId?: string | undefined) => LegacyAudienceObject'.
export const selectMultientityWorkflowAudienceAsLegacyAudience: (
  state: ReduxState,
  page: string,
  workflowId?: string,
) => LegacyAudienceObject = createSelector(
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'page' implicitly has an 'any' type. | TS7006 - Parameter 'workflowId' implicitly has an 'any' type.
  (state, page, workflowId) =>
    state.multientityAudience.workflows[workflowId] &&
    state.multientityAudience.workflows[workflowId][page],
  entities,
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'page' implicitly has an 'any' type.
  (state, page) => page,
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'page' implicitly has an 'any' type. | TS7006 - Parameter 'workflowId' implicitly has an 'any' type.
  (state, page, workflowId) => workflowId,
  (audiencePage, entities, page, workflowId) =>
    audiencePage
      ? {
          // TODO(marcos): replace this with a better implementation
          memberList: audiencePage.entitiesById.map(
            // @ts-expect-error - TS7006 - Parameter 'entityId' implicitly has an 'any' type.
            (entityId) => entities[entityId],
          ),
          numPages: audiencePage.numPages,
          numTotalMembers: audiencePage.totalEntities,
          page: parseInt(page, 10),
          workflowId,
        }
      : null,
);

// @ts-expect-error - TS2769 - No overload matches this call.
export const selectMultiEntityAudienceMember = createSelector<
  ReduxState,
  string,
  ModifiedLegacyAudienceMember | null | undefined,
  AudienceEntity
>(selectEntity, (entity) => (entity ? entityAsLegacyEntity(entity) : null));

type AudienceMemberField = {
  isReadOnly: boolean;
  key: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'currency';
  value?: any;
};

export const selectAudienceMemberAsFields: (
  state: ReduxState,
  id: string,
  entityType: EntityType,
) => AudienceMemberField[] = createSelector(
  selectEntity,
  (state, _id, entityType) =>
    selectEntityDirectAttributesByType(state, entityType),
  (entity, candidateAttributes) =>
    entity?.data
      ? candidateAttributes.map((attribute) => ({
          label: attribute.display_name,
          key: attribute.attribute_name,
          isReadOnly: true,
          type: 'string',
          value: entity.data[attribute.attribute_name] ?? null,
          dataType: attribute.data_type,
          category: attribute.category,
          others: attribute,
        }))
      : [],
);

/**
 * - gets and formats related entities of a person entity
 * - entityType here is ats entity type (e.g. 'bh_placement')
 * - data attribute in the return includes only variables of the related entity;
 *   for example, if the entity type is bh_placement, data would include placement variables only
 * - an example return with one related entity: [
 *   {
        "submission_date": "2019-02-03",
        "company_id": null,
        "company_name": null,
        "candidate_id": "01b9d017-54be-402d-9a1c-6697ee6cbd0f",
        "candidate_name": "Chris Pryor",
        "data": {
          "id": {
            "name": "id",
            "value": "132",
            "type": "string",
            "label": "id"
          },
          "latestappointment.id": {
            "name": "latestappointment.id",
            "value": null,
            "type": "string",
            "label": "latestappointment.id"
          }
          ...
        },
        "external_source_id": "132",
        "id": "0812c0fc-a127-401d-a66a-4817fcc27cf6"
      }
    ]
 */
export const selectRelatedEntitiesAsSummaries = createSelector<
  // State
  ReduxState, // Props
  string,
  EntitySummary[],
  {
    [id: string]: EntitySummary;
  }, // T2
  string[], // T3
  EntityAttribute[]
>( // T1
  (state, _, entityType) =>
    state.multientityAudience.related?.[entityType] || emptyObject, // T2
  (state, audienceMemberId, entityType) =>
    state.multientityAudience.incoming?.[audienceMemberId]?.[entityType] ||
    emptyArray, // T3
  (state, _, entityType) =>
    // @ts-expect-error - TS2322 - Type 'EntityAttributeSchema[]' is not assignable to type 'string[]'.
    selectEntityDirectAttributesByType(state, entityType),
  (relatedEntityDict, entityList, atsEntityAttributes) =>
    entityList
      .map((id) => {
        // @ts-expect-error - TS2538 - Type 'ContactSummary' cannot be used as an index type. | TS2538 - Type 'InternalEmployeeSummary' cannot be used as an index type. | TS2538 - Type 'JobSummary' cannot be used as an index type. | TS2538 - Type 'PersonRelatedSummary' cannot be used as an index type. | TS2538 - Type 'PlacementSummary' cannot be used as an index type. | TS2538 - Type 'RelatedSummary' cannot be used as an index type. | TS2538 - Type 'SubmissionSummary' cannot be used as an index type.
        const relatedEntity = relatedEntityDict[id];
        if (relatedEntity) {
          const data = keyBy(
            // @ts-expect-error - TS2349 - This expression is not callable. | TS7006 - Parameter 'attribute' implicitly has an 'any' type.
            atsEntityAttributes.map((attribute) => ({
              name: attribute.attribute_name,
              value:
                relatedEntity.data[attribute.attribute_name?.toLowerCase()] ??
                null,
              //NOTE (angelina): lowercasing attribute name because server sends back variable values with keys lowercased
              type: attribute.data_type,
              label: attribute.display_name,
            })),
            'name',
          );

          return {
            ...relatedEntity,
            id: relatedEntity.id,
            data,
          };
        }

        return null;
      })
      .filter(Boolean),
);

export const selectRelatedEntityIds: (
  state: ReduxState,
  entityType: EntityType,
  audienceMemberId: string,
) => string[] = createSelector(
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'entityType' implicitly has an 'any' type.
  (_, entityType) => entityType,
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter '__' implicitly has an 'any' type. | TS7006 - Parameter 'audienceMemberId' implicitly has an 'any' type.
  (_, __, audienceMemberId) => audienceMemberId,
  getIncomingEntities,
  (entityType, audienceMemberId, incomingEntities) => {
    const relatedEntities = get(
      incomingEntities,
      [audienceMemberId, entityType],
      [],
    );
    return relatedEntities;
  },
);

export const selectRelatedEntitySummaries: (
  state: ReduxState,
  entityType: EntityType,
  audienceMemberId: string,
) => PlacementSummary[] = createSelector(
  selectRelatedEntityIds,
  (state, entityType) =>
    state.multientityAudience.related?.[entityType] || emptyObject,
  (entityIds, relatedEntities) =>
    entityIds.map((entityId) => relatedEntities[entityId]).filter(Boolean),
);

export const selectPersonEntitySummaries: (
  state: ReduxState,
  personId: string,
  entityUrl: string,
) => PersonRelatedSummary = (state, personId, entityUrl) => {
  const mappingsByUrl = getAtsMappingsByUrl(state);
  const entityType = mappingsByUrl[entityUrl]?.name;
  return state.multientityAudience.related[entityType]?.[personId] || {};
};

/**
 * returns a list of all related entities that have already been fetched. This is useful for avoiding refetching entities already fetched.
 */
export const selectAllRelatedEntitiesIds: (state: ReduxState) => string[] =
  createSelector(selectRelatedEntities, (relatedEntities) =>
    Object.keys(relatedEntities).reduce<Array<any>>(
      (ids, entityType) => ids.concat(Object.keys(relatedEntities[entityType])),
      [],
    ),
  );

export const selectRelatedEntityByExternalId: (
  state: ReduxState,
  entityType: EntityType,
  externalId: string,
) => EntitySummary = createSelector(
  selectRelatedEntities,
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'entityType' implicitly has an 'any' type.
  (_, entityType) => entityType,
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter '__' implicitly has an 'any' type. | TS7006 - Parameter 'externalId' implicitly has an 'any' type.
  (_, __, externalId) => externalId,
  (relatedEntities, entityType, externalId) => {
    const entities = relatedEntities[entityType];
    return (
      (entities &&
        values(entities).find(
          // @ts-expect-error - TS2769 - No overload matches this call.
          ({external_source_id}) => external_source_id === externalId,
        )) ||
      {}
    );
  },
);
